import { ServiceTypes } from 'constants/ServiceTypes';
import { ReactComponent as Back } from 'images/strikezone/homeplates/Back.svg';
import { ReactComponent as Front } from 'images/strikezone/homeplates/Front.svg';
import { ReactComponent as FrontBack } from 'images/strikezone/homeplates/FrontBack.svg';
import { ReactComponent as FrontMiddle } from 'images/strikezone/homeplates/FrontMiddle.svg';
import { ReactComponent as FrontMiddleBack } from 'images/strikezone/homeplates/FrontMiddleBack.svg';
import { ReactComponent as Middle } from 'images/strikezone/homeplates/Middle.svg';
import { ReactComponent as MiddleBack } from 'images/strikezone/homeplates/MiddleBack.svg';
import { ReactComponent as BackExtensionBack } from 'images/strikezone/homeplates/withExtension/BackExtensionBack.svg';
import { ReactComponent as FrontMiddleBackExtensionBack } from 'images/strikezone/homeplates/withExtension/FrontMiddleBackExtensionBack.svg';
import { ReactComponent as FrontMiddleBackExtensionMiddle } from 'images/strikezone/homeplates/withExtension/FrontMiddleBackExtensionMiddle.svg';
import { ReactComponent as FrontMiddleBackExtensionMiddleBack } from 'images/strikezone/homeplates/withExtension/FrontMiddleBackExtensionMiddleBack.svg';
import { ReactComponent as FrontMiddleExtensionMiddle } from 'images/strikezone/homeplates/withExtension/FrontMiddleExtensionMiddle.svg';
import { ReactComponent as MiddleBackExtensionBack } from 'images/strikezone/homeplates/withExtension/MiddleBackExtensionBack.svg';
import { ReactComponent as MiddleBackExtensionMiddle } from 'images/strikezone/homeplates/withExtension/MiddleBackExtensionMiddle.svg';
import { ReactComponent as MiddleBackExtensionMiddleBack } from 'images/strikezone/homeplates/withExtension/MiddleBackExtensionMiddleBack.svg';
import { ReactComponent as MiddleExtensionMiddle } from 'images/strikezone/homeplates/withExtension/MiddleExtensionMiddle.svg';
import { ReactComponent as BackOffsetBack } from 'images/strikezone/homeplates/withOffset/BackOffsetBack.svg';
import { ReactComponent as FrontBackOffsetBack } from 'images/strikezone/homeplates/withOffset/FrontBackOffsetBack.svg';
import { ReactComponent as FrontMiddleBackOffsetBack } from 'images/strikezone/homeplates/withOffset/FrontMiddleBackOffsetBack.svg';
import { ReactComponent as FrontMiddleBackOffsetMiddle } from 'images/strikezone/homeplates/withOffset/FrontMiddleBackOffsetMiddle.svg';
import { ReactComponent as FrontMiddleBackOffsetMiddleBack } from 'images/strikezone/homeplates/withOffset/FrontMiddleBackOffsetMiddleBack.svg';
import { ReactComponent as FrontMiddleOffsetMiddle } from 'images/strikezone/homeplates/withOffset/FrontMiddleOffsetMiddle.svg';
import { ReactComponent as MiddleBackOffsetBack } from 'images/strikezone/homeplates/withOffset/MiddleBackOffsetBack.svg';
import { ReactComponent as MiddleBackOffsetMiddle } from 'images/strikezone/homeplates/withOffset/MiddleBackOffsetMiddle.svg';
import { ReactComponent as MiddleBackOffsetMiddleBack } from 'images/strikezone/homeplates/withOffset/MiddleBackOffsetMiddleBack.svg';
import { ReactComponent as MiddleOffsetMiddle } from 'images/strikezone/homeplates/withOffset/MiddleOffsetMiddle.svg';
import { StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { StrikeZoneCalculationInformations } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';

import { ReactComponent as FrontMiddleBackExtensionMiddleOffsetBack } from 'images/strikezone/homeplates/withOffsetAndExtension/FrontMiddleBackExtensionMiddleOffsetBack.svg';
import { ReactComponent as FrontMiddleBackOffsetMiddleExtensionBack } from 'images/strikezone/homeplates/withOffsetAndExtension/FrontMiddleBackOffsetMiddleExtensionBack.svg';
import { ReactComponent as MiddleBackExtensionMiddleOffsetBack } from 'images/strikezone/homeplates/withOffsetAndExtension/MiddleBackExtensionMiddleOffsetBack.svg';
import { ReactComponent as MiddleBackOffsetMiddleExtensionBack } from 'images/strikezone/homeplates/withOffsetAndExtension/MiddleBackOffsetMiddleExtensionBack.svg';

export const HomeplateImage = observer(() => {
  const { league } = useInjection<LineupStore>(ServiceTypes.LineupStore);

  const checkOffsetExist = (calculationType: StrikeZoneLocation, calculations: StrikeZoneCalculationInformations[]) => {
    return calculations.find(
      (calculation) => calculation.strikeZoneCalculationType === calculationType && calculation.offset !== 0 && calculation.offset !== '0.0'
    );
  };

  const checkExtensionExist = (calculationType: StrikeZoneLocation, calculations: StrikeZoneCalculationInformations[]) => {
    return calculations.find(
      (calculation) =>
        calculation.strikeZoneCalculationType === calculationType && calculation.extension !== 0 && calculation.extension !== '0.0'
    );
  };

  const getHomePlateImage = () => {
    const calculations = league.strikeZoneCalculationInformations;

    if (!calculations || calculations.length === 0) {
      return;
    }

    const singleImageMap = {
      Front: <Front />,
      Middle: <Middle />,
      Back: <Back />,
    };

    if (calculations.length === 1 && calculations[0].strikeZoneCalculationType) {
      if (calculations[0].strikeZoneCalculationType === StrikeZoneLocation.Middle) {
        if (checkOffsetExist(StrikeZoneLocation.Middle, calculations)) {
          return <MiddleOffsetMiddle />;
        } else if (checkExtensionExist(StrikeZoneLocation.Middle, calculations)) {
          return <MiddleExtensionMiddle />;
        }
      }

      if (calculations[0].strikeZoneCalculationType === StrikeZoneLocation.Back) {
        if (checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
          return <BackOffsetBack />;
        } else if (checkExtensionExist(StrikeZoneLocation.Back, calculations)) {
          return <BackExtensionBack />;
        }
      }

      return singleImageMap[calculations[0].strikeZoneCalculationType];
    }

    if (calculations.length > 1) {
      const calculationTypes = calculations.map((calculation) => calculation.strikeZoneCalculationType);

      if (calculationTypes.length === 2) {
        if (calculationTypes.includes(StrikeZoneLocation.Front) && calculationTypes.includes(StrikeZoneLocation.Middle)) {
          if (checkOffsetExist(StrikeZoneLocation.Middle, calculations)) {
            return <FrontMiddleOffsetMiddle />;
          } else if (checkExtensionExist(StrikeZoneLocation.Middle, calculations)) {
            return <FrontMiddleExtensionMiddle />;
          }
          return <FrontMiddle />;
        }
        if (calculationTypes.includes(StrikeZoneLocation.Front) && calculationTypes.includes(StrikeZoneLocation.Back)) {
          if (checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
            return <FrontBackOffsetBack />;
          } else if (checkExtensionExist(StrikeZoneLocation.Back, calculations)) {
            return <FrontMiddleBackExtensionBack />;
          }
          return <FrontBack />;
        }

        if (calculationTypes.includes(StrikeZoneLocation.Middle) && calculationTypes.includes(StrikeZoneLocation.Back)) {
          if (checkOffsetExist(StrikeZoneLocation.Middle, calculations) && checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
            return <MiddleBackOffsetMiddleBack />;
          } else if (
            checkExtensionExist(StrikeZoneLocation.Middle, calculations) &&
            checkExtensionExist(StrikeZoneLocation.Back, calculations)
          ) {
            return <MiddleBackExtensionMiddleBack />;
          } else if (
            checkOffsetExist(StrikeZoneLocation.Middle, calculations) &&
            checkExtensionExist(StrikeZoneLocation.Back, calculations)
          ) {
            return <MiddleBackOffsetMiddleExtensionBack />;
          } else if (
            checkExtensionExist(StrikeZoneLocation.Middle, calculations) &&
            checkOffsetExist(StrikeZoneLocation.Back, calculations)
          ) {
            return <MiddleBackExtensionMiddleOffsetBack />;
          }

          if (checkOffsetExist(StrikeZoneLocation.Middle, calculations)) {
            return <MiddleBackOffsetMiddle />;
          } else if (checkExtensionExist(StrikeZoneLocation.Middle, calculations)) {
            return <MiddleBackExtensionMiddle />;
          }
          if (checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
            return <MiddleBackOffsetBack />;
          } else if (checkExtensionExist(StrikeZoneLocation.Back, calculations)) {
            return <MiddleBackExtensionBack />;
          }
          return <MiddleBack />;
        }
      }

      if (checkOffsetExist(StrikeZoneLocation.Middle, calculations) && checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
        return <FrontMiddleBackOffsetMiddleBack />;
      } else if (
        checkExtensionExist(StrikeZoneLocation.Middle, calculations) &&
        checkExtensionExist(StrikeZoneLocation.Back, calculations)
      ) {
        return <FrontMiddleBackExtensionMiddleBack />;
      } else if (checkOffsetExist(StrikeZoneLocation.Middle, calculations) && checkExtensionExist(StrikeZoneLocation.Back, calculations)) {
        return <FrontMiddleBackOffsetMiddleExtensionBack />;
      } else if (checkExtensionExist(StrikeZoneLocation.Middle, calculations) && checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
        return <FrontMiddleBackExtensionMiddleOffsetBack />;
      }
      if (checkOffsetExist(StrikeZoneLocation.Middle, calculations)) {
        return <FrontMiddleBackOffsetMiddle />;
      } else if (checkExtensionExist(StrikeZoneLocation.Middle, calculations)) {
        return <FrontMiddleBackExtensionMiddle />;
      }
      if (checkOffsetExist(StrikeZoneLocation.Back, calculations)) {
        return <FrontMiddleBackOffsetBack />;
      } else if (checkExtensionExist(StrikeZoneLocation.Back, calculations)) {
        return <FrontMiddleBackExtensionBack />;
      }

      return <FrontMiddleBack />;
    }
  };

  return <>{getHomePlateImage()}</>;
});
